import { Component, OnInit } from '@angular/core'
import { collection, deleteDoc, doc, getDocs, query, setDoc, updateDoc, where } from '@angular/fire/firestore'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { Administrator, CrmAssignee, FirestoreStructure, SalesManager } from '@parkupp/core'
import { AdminService } from 'src/app/services/admin.service'
import Swal from 'sweetalert2'

interface UserLookupResult {
    uid: string
    email: string
    firstName?: string
    lastName?: string
    displayName: string
}

@Component({
    selector: 'app-admin-users-list',
    templateUrl: './admin-users-list.component.html',
    styleUrls: ['./admin-users-list.component.scss'],
})
export class AdminUsersListComponent implements OnInit {
    administrators: Administrator[] = []
    salesManagers: SalesManager[] = []
    crmAssignees: CrmAssignee[] = []
    loading = true

    // User lookup cache - maps UID to user info
    userInfoCache: { [uid: string]: UserLookupResult } = {}

    // Add Administrator Modal
    showAddAdministratorModal = false
    addAdministratorForm = new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email]),
    })
    isAddingAdministrator = false
    administratorLookupResult: UserLookupResult | null = null
    administratorLookupError: string | null = null

    // Add Sales Manager Modal
    showAddSalesManagerModal = false
    addSalesManagerForm = new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email]),
    })
    isAddingSalesManager = false
    salesManagerLookupResult: UserLookupResult | null = null
    salesManagerLookupError: string | null = null

    // Add CRM Assignee Modal
    showAddCrmAssigneeModal = false
    addCrmAssigneeForm = new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email]),
        displayName: new FormControl('', [Validators.required]),
        badgeColor: new FormControl('#6c757d'),
    })
    isAddingCrmAssignee = false
    crmAssigneeLookupResult: UserLookupResult | null = null
    crmAssigneeLookupError: string | null = null

    // Preset badge colors
    badgeColors = [
        { name: 'Gray', color: '#6c757d' },
        { name: 'Blue', color: '#0d6efd' },
        { name: 'Green', color: '#198754' },
        { name: 'Red', color: '#dc3545' },
        { name: 'Orange', color: '#fd7e14' },
        { name: 'Purple', color: '#6f42c1' },
        { name: 'Teal', color: '#20c997' },
        { name: 'Pink', color: '#d63384' },
        { name: 'Yellow', color: '#ffc107' },
        { name: 'Cyan', color: '#0dcaf0' },
    ]

    constructor(private adminService: AdminService) {}

    async ngOnInit() {
        await this.loadData()
    }

    async loadData() {
        this.loading = true
        await Promise.all([this.loadAdministrators(), this.loadSalesManagers(), this.loadCrmAssignees()])
        // Load user info for all UIDs
        await this.loadUserInfoForAllUsers()
        this.loading = false
    }

    async loadAdministrators() {
        const snapshot = await getDocs(collection(this.adminService.firestore, FirestoreStructure.ADMINISTRATORS))
        this.administrators = snapshot.docs.map((doc) => {
            const admin = new Administrator()
            admin.$key = doc.id
            admin.active = doc.data()['active'] !== false
            return admin
        })
    }

    async loadSalesManagers() {
        const snapshot = await getDocs(collection(this.adminService.firestore, FirestoreStructure.SALES_MANAGERS))
        this.salesManagers = snapshot.docs.map((doc) => new SalesManager(doc.data(), doc.id))
    }

    async loadCrmAssignees() {
        const snapshot = await getDocs(collection(this.adminService.firestore, FirestoreStructure.CRM_ASSIGNEES))
        this.crmAssignees = snapshot.docs.map((doc) => new CrmAssignee(doc.data(), doc.id))
    }

    async loadUserInfoForAllUsers() {
        // Collect all unique UIDs
        const uids = new Set<string>()
        this.administrators.forEach((a) => a.$key && uids.add(a.$key))
        this.salesManagers.forEach((sm) => sm.$key && uids.add(sm.$key))
        this.crmAssignees.forEach((ca) => ca.$key && uids.add(ca.$key))

        // Look up user info for each UID
        for (const uid of uids) {
            if (!this.userInfoCache[uid]) {
                const userInfo = await this.lookupUserByUid(uid)
                if (userInfo) {
                    this.userInfoCache[uid] = userInfo
                }
            }
        }
    }

    async lookupUserByUid(uid: string): Promise<UserLookupResult | null> {
        try {
            const snapshot = await getDocs(query(collection(this.adminService.firestore, FirestoreStructure.ADMIN_CLIENTS), where('__name__', '==', uid)))

            if (!snapshot.empty) {
                const doc = snapshot.docs[0]
                const data = doc.data()
                return {
                    uid: doc.id,
                    email: data['email'] || '',
                    firstName: data['firstName'],
                    lastName: data['lastName'],
                    displayName: data['firstName'] && data['lastName'] ? `${data['firstName']} ${data['lastName']}` : data['email'] || doc.id,
                }
            }
            return null
        } catch (error) {
            console.error('Error looking up user by UID:', error)
            return null
        }
    }

    async lookupUserByEmail(email: string): Promise<UserLookupResult | null> {
        try {
            const snapshot = await getDocs(query(collection(this.adminService.firestore, FirestoreStructure.ADMIN_CLIENTS), where('email', '==', email.toLowerCase().trim())))

            if (!snapshot.empty) {
                const doc = snapshot.docs[0]
                const data = doc.data()
                return {
                    uid: doc.id,
                    email: data['email'] || email,
                    firstName: data['firstName'],
                    lastName: data['lastName'],
                    displayName: data['firstName'] && data['lastName'] ? `${data['firstName']} ${data['lastName']}` : email,
                }
            }
            return null
        } catch (error) {
            console.error('Error looking up user by email:', error)
            return null
        }
    }

    getUserDisplayInfo(uid: string): { email: string; name: string } {
        const cached = this.userInfoCache[uid]
        if (cached) {
            return { email: cached.email, name: cached.displayName }
        }
        return { email: '', name: uid }
    }

    // Administrator Methods
    openAddAdministratorModal() {
        this.showAddAdministratorModal = true
        this.addAdministratorForm.reset()
        this.administratorLookupResult = null
        this.administratorLookupError = null
    }

    closeAddAdministratorModal() {
        this.showAddAdministratorModal = false
        this.isAddingAdministrator = false
        this.administratorLookupResult = null
        this.administratorLookupError = null
    }

    async lookupAdministratorByEmail() {
        const email = this.addAdministratorForm.get('email')?.value?.trim()
        if (!email) return

        this.administratorLookupResult = null
        this.administratorLookupError = null

        const result = await this.lookupUserByEmail(email)
        if (result) {
            // Check if already an administrator
            if (this.administrators.some((a) => a.$key === result.uid)) {
                this.administratorLookupError = 'This user is already an administrator.'
            } else {
                this.administratorLookupResult = result
            }
        } else {
            this.administratorLookupError = 'No user found with this email address. The user must have signed up first.'
        }
    }

    async addAdministrator() {
        if (!this.administratorLookupResult || this.isAddingAdministrator) return

        this.isAddingAdministrator = true
        const uid = this.administratorLookupResult.uid

        try {
            await setDoc(doc(this.adminService.firestore, FirestoreStructure.ADMINISTRATORS, uid), { active: true })

            // Cache the user info
            this.userInfoCache[uid] = this.administratorLookupResult

            this.closeAddAdministratorModal()
            await this.loadAdministrators()

            Swal.fire({
                icon: 'success',
                title: 'Administrator Added',
                text: `${this.administratorLookupResult.displayName} has been added as an Administrator.`,
                timer: 2000,
                showConfirmButton: false,
            })
        } catch (error) {
            console.error('Error adding administrator:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to add administrator. Please try again.',
            })
        } finally {
            this.isAddingAdministrator = false
        }
    }

    async toggleAdministratorActive(admin: Administrator) {
        try {
            await updateDoc(doc(this.adminService.firestore, FirestoreStructure.ADMINISTRATORS, admin.$key!), {
                active: !admin.active,
            })
            admin.active = !admin.active
        } catch (error) {
            console.error('Error toggling administrator active:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to update administrator. Please try again.',
            })
        }
    }

    async deleteAdministrator(admin: Administrator) {
        const userInfo = this.getUserDisplayInfo(admin.$key!)
        const result = await Swal.fire({
            title: 'Remove Administrator?',
            text: `Are you sure you want to remove administrator access from ${userInfo.name}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, remove',
        })

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(this.adminService.firestore, FirestoreStructure.ADMINISTRATORS, admin.$key!))
                await this.loadAdministrators()
                Swal.fire({
                    icon: 'success',
                    title: 'Removed',
                    text: 'Administrator access has been removed.',
                    timer: 1500,
                    showConfirmButton: false,
                })
            } catch (error) {
                console.error('Error deleting administrator:', error)
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to remove administrator. Please try again.',
                })
            }
        }
    }

    // Sales Manager Methods
    openAddSalesManagerModal() {
        this.showAddSalesManagerModal = true
        this.addSalesManagerForm.reset()
        this.salesManagerLookupResult = null
        this.salesManagerLookupError = null
    }

    closeAddSalesManagerModal() {
        this.showAddSalesManagerModal = false
        this.isAddingSalesManager = false
        this.salesManagerLookupResult = null
        this.salesManagerLookupError = null
    }

    async lookupSalesManagerByEmail() {
        const email = this.addSalesManagerForm.get('email')?.value?.trim()
        if (!email) return

        this.salesManagerLookupResult = null
        this.salesManagerLookupError = null

        const result = await this.lookupUserByEmail(email)
        if (result) {
            // Check if already a sales manager
            if (this.salesManagers.some((sm) => sm.$key === result.uid)) {
                this.salesManagerLookupError = 'This user is already a sales manager.'
            } else {
                this.salesManagerLookupResult = result
            }
        } else {
            this.salesManagerLookupError = 'No user found with this email address. The user must have signed up first.'
        }
    }

    async addSalesManager() {
        if (!this.salesManagerLookupResult || this.isAddingSalesManager) return

        this.isAddingSalesManager = true
        const uid = this.salesManagerLookupResult.uid

        try {
            const salesManager = new SalesManager({ active: true })
            await setDoc(doc(this.adminService.firestore, FirestoreStructure.SALES_MANAGERS, uid), salesManager.parse())

            // Cache the user info
            this.userInfoCache[uid] = this.salesManagerLookupResult

            this.closeAddSalesManagerModal()
            await this.loadSalesManagers()

            Swal.fire({
                icon: 'success',
                title: 'Sales Manager Added',
                text: `${this.salesManagerLookupResult.displayName} has been added as a Sales Manager.`,
                timer: 2000,
                showConfirmButton: false,
            })
        } catch (error) {
            console.error('Error adding sales manager:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to add sales manager. Please try again.',
            })
        } finally {
            this.isAddingSalesManager = false
        }
    }

    async toggleSalesManagerActive(salesManager: SalesManager) {
        try {
            await updateDoc(doc(this.adminService.firestore, FirestoreStructure.SALES_MANAGERS, salesManager.$key!), {
                active: !salesManager.active,
            })
            salesManager.active = !salesManager.active
        } catch (error) {
            console.error('Error toggling sales manager active:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to update sales manager. Please try again.',
            })
        }
    }

    async deleteSalesManager(salesManager: SalesManager) {
        const userInfo = this.getUserDisplayInfo(salesManager.$key!)
        const result = await Swal.fire({
            title: 'Remove Sales Manager?',
            text: `Are you sure you want to remove the sales manager role from ${userInfo.name}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, remove',
        })

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(this.adminService.firestore, FirestoreStructure.SALES_MANAGERS, salesManager.$key!))
                await this.loadSalesManagers()
                Swal.fire({
                    icon: 'success',
                    title: 'Removed',
                    text: 'Sales manager role has been removed.',
                    timer: 1500,
                    showConfirmButton: false,
                })
            } catch (error) {
                console.error('Error deleting sales manager:', error)
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to remove sales manager. Please try again.',
                })
            }
        }
    }

    // CRM Assignee Methods
    openAddCrmAssigneeModal() {
        this.showAddCrmAssigneeModal = true
        this.addCrmAssigneeForm.reset()
        this.addCrmAssigneeForm.get('badgeColor')?.setValue('#6c757d') // Default gray
        this.crmAssigneeLookupResult = null
        this.crmAssigneeLookupError = null
    }

    closeAddCrmAssigneeModal() {
        this.showAddCrmAssigneeModal = false
        this.isAddingCrmAssignee = false
        this.crmAssigneeLookupResult = null
        this.crmAssigneeLookupError = null
    }

    async lookupCrmAssigneeByEmail() {
        const email = this.addCrmAssigneeForm.get('email')?.value?.trim()
        if (!email) return

        this.crmAssigneeLookupResult = null
        this.crmAssigneeLookupError = null

        const result = await this.lookupUserByEmail(email)
        if (result) {
            // Check if already a CRM assignee
            if (this.crmAssignees.some((ca) => ca.$key === result.uid)) {
                this.crmAssigneeLookupError = 'This user is already a CRM assignee.'
            } else {
                this.crmAssigneeLookupResult = result
                // Pre-fill the display name
                this.addCrmAssigneeForm.get('displayName')?.setValue(result.displayName)
            }
        } else {
            this.crmAssigneeLookupError = 'No user found with this email address. The user must have signed up first.'
        }
    }

    async addCrmAssignee() {
        if (!this.crmAssigneeLookupResult || !this.addCrmAssigneeForm.valid || this.isAddingCrmAssignee) return

        this.isAddingCrmAssignee = true
        const uid = this.crmAssigneeLookupResult.uid
        const displayName = this.addCrmAssigneeForm.get('displayName')?.value?.trim()
        const badgeColor = this.addCrmAssigneeForm.get('badgeColor')?.value || '#6c757d'

        try {
            const crmAssignee = new CrmAssignee({ displayName, badgeColor, active: true })
            await setDoc(doc(this.adminService.firestore, FirestoreStructure.CRM_ASSIGNEES, uid), crmAssignee.parse())

            // Cache the user info
            this.userInfoCache[uid] = this.crmAssigneeLookupResult

            this.closeAddCrmAssigneeModal()
            await this.loadCrmAssignees()

            Swal.fire({
                icon: 'success',
                title: 'CRM Assignee Added',
                text: `${displayName} has been added as a CRM Assignee.`,
                timer: 2000,
                showConfirmButton: false,
            })
        } catch (error) {
            console.error('Error adding CRM assignee:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to add CRM assignee. Please try again.',
            })
        } finally {
            this.isAddingCrmAssignee = false
        }
    }

    async toggleCrmAssigneeActive(assignee: CrmAssignee) {
        try {
            await updateDoc(doc(this.adminService.firestore, FirestoreStructure.CRM_ASSIGNEES, assignee.$key!), {
                active: !assignee.active,
            })
            assignee.active = !assignee.active
        } catch (error) {
            console.error('Error toggling CRM assignee active:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to update CRM assignee. Please try again.',
            })
        }
    }

    async updateCrmAssigneeDisplayName(assignee: CrmAssignee, newName: string) {
        if (!newName.trim()) return

        try {
            await updateDoc(doc(this.adminService.firestore, FirestoreStructure.CRM_ASSIGNEES, assignee.$key!), {
                displayName: newName.trim(),
            })
            assignee.displayName = newName.trim()
        } catch (error) {
            console.error('Error updating CRM assignee display name:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to update display name. Please try again.',
            })
        }
    }

    async updateCrmAssigneeBadgeColor(assignee: CrmAssignee, newColor: string) {
        try {
            await updateDoc(doc(this.adminService.firestore, FirestoreStructure.CRM_ASSIGNEES, assignee.$key!), {
                badgeColor: newColor,
            })
            assignee.badgeColor = newColor
        } catch (error) {
            console.error('Error updating CRM assignee badge color:', error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to update badge color. Please try again.',
            })
        }
    }

    async deleteCrmAssignee(assignee: CrmAssignee) {
        const result = await Swal.fire({
            title: 'Remove CRM Assignee?',
            text: `Are you sure you want to remove ${assignee.displayName} from the CRM assignees list?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, remove',
        })

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(this.adminService.firestore, FirestoreStructure.CRM_ASSIGNEES, assignee.$key!))
                await this.loadCrmAssignees()
                Swal.fire({
                    icon: 'success',
                    title: 'Removed',
                    text: 'CRM assignee has been removed.',
                    timer: 1500,
                    showConfirmButton: false,
                })
            } catch (error) {
                console.error('Error deleting CRM assignee:', error)
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to remove CRM assignee. Please try again.',
                })
            }
        }
    }
}

